import { useAuth } from "@clerk/react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { z } from "zod"
import { createTanstackEndpoint } from "../utils/create-endpoint"
import { endpoints } from "../api/endpoints"
import {
  accessRetryDelay,
  classifyAccessFailure,
  isFailedAccess,
  loadAccess,
} from "../utils/access-failure"

const permissionsSchema = z.object({
  userId: z.string().min(1),
  organizationId: z.string().min(1),
  roles: z.array(z.object({ id: z.string(), name: z.string(), permissions: z.array(z.string()) })),
  allPermissions: z.array(z.string()),
  ssoEnabled: z.boolean(),
})

type UserPermissionsResponse = z.infer<typeof permissionsSchema>

const permissionsEndpoint = createTanstackEndpoint(endpoints.users.permissions, "GET")

export function useUserPermissions() {
  const { isSignedIn, isLoaded } = useAuth()
  const queryClient = useQueryClient()

  const { data, isLoading, error, failureReason, isPaused, isFetching, refetch } = useQuery({
    queryKey: ["user-permissions"],
    queryFn: () =>
      loadAccess(
        async (): Promise<UserPermissionsResponse> =>
          permissionsSchema.parse(await permissionsEndpoint.query()),
        (failure) => {
          queryClient.setQueryData(["user-permissions"], failure)
        },
      ),
    enabled: isLoaded && isSignedIn, // Wait for Clerk to load AND user to be signed in
    retryDelay: accessRetryDelay,
    staleTime: 1 * 60 * 1000, // 1 minute
    refetchInterval: 15 * 60 * 1000, // auto-refresh every 15 minutes
  })

  const permissions = isFailedAccess(data) ? undefined : data
  const accessFailure = isFailedAccess(data)
    ? data.accessFailure
    : !data && (error || failureReason || isPaused)
      ? classifyAccessFailure(error ?? failureReason)
      : undefined

  return {
    permissions: permissions?.allPermissions || [],
    roles: permissions?.roles || [],
    hasPermission: (permission: string) =>
      permissions?.allPermissions.includes(permission) || false,
    hasRole: (roleId: string) => permissions?.roles.some((r) => r.id === roleId) || false,
    ssoEnabled: permissions?.ssoEnabled || false,
    isLoading,
    error,
    accessDenied: accessFailure?.kind === "denied",
    accessFailure,
    isVerifyingAccess: isFetching,
    retryAccess: () => {
      void refetch()
    },
  }
}
